import { ChartSvg } from "@/components/tool-kit/charts/chart-svg";
import { CHART_COLORS, CHART_INK } from "@/lib/tool-kit/palette";

const [BLUE, ORANGE] = CHART_COLORS;
const CENTER_X = 280;
const CENTER_Y = 112;
const BODY_RADIUS = 44;
/** Angle of the satellite, measured from the +x axis, drawn up and to the right. */
const ANGLE = (-35 * Math.PI) / 180;

/** Trig results can differ in the last digit between Node and the browser; round them. */
const round = (value: number): number => Math.round(value * 100) / 100;

/**
 * A schematic circular orbit: the body, the orbit, the satellite with its velocity arrow
 * and the radius r. The orbit's size follows r/R but is clamped, so it is not to scale.
 */
export function OrbitDiagram({
  id,
  label,
  description,
  ratio,
}: {
  id: string;
  label: string;
  description: string;
  /** Orbital radius as a multiple of the body's radius (any positive number). */
  ratio: number;
}) {
  const orbit = BODY_RADIUS * Math.min(Math.max(ratio, 1.3), 2.35);
  const sx = round(CENTER_X + orbit * Math.cos(ANGLE));
  const sy = round(CENTER_Y + orbit * Math.sin(ANGLE));
  // Counter-clockwise on screen: the tangent at the satellite.
  const tx = Math.round(Math.sin(ANGLE) * 1e4) / 1e4;
  const ty = Math.round(-Math.cos(ANGLE) * 1e4) / 1e4;
  const arrow = `${id}-arrow`;
  return (
    <ChartSvg
      description={description}
      height={224}
      id={id}
      label={label}
      width={560}
    >
      <defs>
        <marker
          id={arrow}
          markerHeight="8"
          markerWidth="8"
          orient="auto"
          refX="7"
          refY="4"
        >
          <path d="M0,0 L8,4 L0,8 z" fill={CHART_INK.text} />
        </marker>
      </defs>
      <circle
        cx={CENTER_X}
        cy={CENTER_Y}
        fill="none"
        r={orbit}
        stroke={CHART_INK.axis}
        strokeDasharray="5 4"
        strokeWidth="1.5"
      />
      <circle
        cx={CENTER_X}
        cy={CENTER_Y}
        fill={BLUE}
        fillOpacity="0.85"
        r={BODY_RADIUS}
      />
      <line
        stroke={CHART_INK.muted}
        strokeWidth="1.5"
        x1={CENTER_X}
        x2={sx}
        y1={CENTER_Y}
        y2={sy}
      />
      <circle cx={sx} cy={sy} fill={ORANGE} r={8} />
      <line
        markerEnd={`url(#${arrow})`}
        stroke={CHART_INK.text}
        strokeWidth="2.5"
        x1={sx + tx * 12}
        x2={sx + tx * 62}
        y1={sy + ty * 12}
        y2={sy + ty * 62}
      />
      <text
        fill="#ffffff"
        fontSize="16"
        fontStyle="italic"
        textAnchor="middle"
        x={CENTER_X}
        y={CENTER_Y + 5}
      >
        M
      </text>
      <text
        fill={CHART_INK.text}
        fontSize="15"
        fontStyle="italic"
        textAnchor="middle"
        x={(CENTER_X + sx) / 2 + 8}
        y={(CENTER_Y + sy) / 2 - 8}
      >
        r
      </text>
      <text
        fill={CHART_INK.text}
        fontSize="15"
        fontStyle="italic"
        textAnchor="middle"
        x={sx + tx * 66 + 10}
        y={sy + ty * 66 + 4}
      >
        v
      </text>
    </ChartSvg>
  );
}
